<template>
  <div
    aria-label="Create run sheet"
    role="main"
  >
    <div
      v-if="isLoading === true"
      class="text-center mb-2"
    >
      <b-spinner
        variant="primary"
        label="Text Centered"
      />
    </div>
    <b-form-group
      v-if="user.role === 'admin' || user.role === 'superadmin'"
      label="Dépôt"
      label-for="select-repository"
      aria-label="Sélection du dépôt"
    >
      <validation-provider
        #default="{ errors }"
        name="Dépôt"
      >
        <v-select
          v-model="repository"
          :clearable="false"
          placeholder="Sélectionner un dépôt"
          label="name"
          :options="repositories"
          input-id="select-repository"
          aria-label="Liste des dépôts disponibles"
          @input="getDepositOrder"
        />
        <small class="text-danger">{{ errors[0] }}</small>
      </validation-provider>
    </b-form-group>
    <b-form-group
      label="Référence du colis à ajouter à la run sheet"
      label-for="input-colis-ref"
      aria-label="Champ de saisie de la référence du colis"
    >
      <b-input-group class="input-group-merge">
        <b-input-group-prepend is-text>
          <feather-icon icon="MaximizeIcon" />
        </b-input-group-prepend>
        <b-form-input
          id="input-colis-ref"
          ref="select"
          v-model="ref"
          placeholder="Scanner ou saisir la référence du colis"
          aria-label="Entrer la référence du colis à ajouter"
          @change="checkOrder"
        />
      </b-input-group>
    </b-form-group>
    <!-- table -->
    <div class="d-flex justify-content-between">
      <div
        v-if="isLoading === true"
        class="text-center mb-2"
      >
        <b-spinner
          variant="primary"
          label="Text Centered"
        />
      </div>
      <b-card>
        <h4 aria-live="polite">
          Colis au dépôt : {{ orders.length }}
        </h4>
        <vue-good-table
          :columns="columns"
          :rows="orders"
          :search-options="{
            enabled: true,
            externalQuery: searchTerm ,
          }"
        >
          <template
            slot="table-row"
            slot-scope="props"
          >
            <span v-if="props.column.field === 'id'">
              <span> {{ props.row.id }}</span><br>
              <span
                v-if="props.row.is_an_exchange"
                style="color:red ; font-weight: bold;"
              > Echange </span>
              <b-badge
                v-if="props.row.status === 18"
                variant="info"
                class="ml-1"
              >Rendez Vous</b-badge>
              <span v-if="props.row.status === 18 && props.row.planned_data">
                <br><small>Prévu: {{ props.row.planned_data }}</small>
              </span>
              <!-- Last Return Reason: small with icon -->
              <div
                v-if="props.row.last_return_reason"
                class="text-wrap mt-1 d-flex align-items-center"
                style="max-width: 200px; font-size: 11px; color: #888;"
              >
                <feather-icon
                  icon="AlertCircleIcon"
                  size="12"
                  class="mr-1"
                  style="color:#e74c3c;"
                />
                <span>
                  {{ props.row.last_return_reason.return_reason }}
                  <span class="text-muted ml-1">({{ formatDate(props.row.last_return_reason.created_at) }})</span>
                  <span
                    v-if="props.row.last_return_reason.scheduled_date"
                    class="text-info ml-1"
                  >
                    Prévu: {{ formatDate(props.row.last_return_reason.scheduled_date) }}
                  </span>
                  <span
                    v-if="props.row.last_return_reason.additional_note"
                    class="text-secondary ml-1"
                  >
                    Note: {{ props.row.last_return_reason.additional_note }}
                  </span>
                </span>
              </div>
            </span>
            <!-- Column: Action -->
            <span v-else-if="props.column.field === 'action'">
              <span>
                <feather-icon
                  :id="`invoice-row-${props.row.id}-show-icon`"
                  icon="CheckSquareIcon"
                  class="cursor-pointer mr-1"
                  size="16"
                  color="green"
                  @click="addItem(props.row)"
                />
                <b-tooltip
                  title="Ajouter"
                  class="cursor-pointer"
                  :target="`invoice-row-${props.row.id}-show-icon`"
                />

              </span>
            </span>

            <!-- Column: Common -->
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
          <!-- pagination -->
          <template
            slot="pagination-bottom"
            slot-scope="props"
          >
            <div class="d-flex justify-content-between flex-wrap">
              <div class="d-flex align-items-center mb-0 mt-1">
                <span class="text-nowrap">
                  Affichage 1 à
                </span>
                <b-form-select
                  v-model="pageLength"
                  :options="['50','100','500']"
                  class="mx-1"
                  @input="(value)=>props.perPageChanged({currentPerPage:value})"
                />
                <span class="text-nowrap "> de {{ props.total }} résultats trouvés </span>
              </div>
              <div>
                <b-pagination
                  :value="1"
                  :total-rows="props.total"
                  :per-page="pageLength"
                  first-number
                  last-number
                  align="right"
                  prev-class="prev-item"
                  next-class="next-item"
                  class="mt-1 mb-0"
                  @input="(value)=>props.pageChanged({currentPage:value})"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </div>
            </div>
          </template>
        </vue-good-table>
      </b-card>
      <b-card class="ml-1">
        <h4 aria-live="polite">
          Colis dans la run sheet : {{ selected.length }}
        </h4>
        <vue-good-table
          :columns="columns"
          :rows="selected"
          :search-options="{
            enabled: true,
            externalQuery: searchTerm }"
        >
          <template
            slot="table-row"
            slot-scope="props"
          >
            <span v-if="props.column.field === 'id'">
              <span> {{ props.row.id }}</span><br>
              <span
                v-if="props.row.is_an_exchange"
                style="color:red ; font-weight: bold;"
              > Echange </span>
              <b-badge
                v-if="props.row.status === 18"
                variant="info"
                class="ml-1"
              >Rendez Vous</b-badge>
            </span>
            <!-- Column: Action -->
            <span v-else-if="props.column.field === 'action'">
              <span>
                <feather-icon
                  :id="`invoice-row-${props.row.id}-show-icon`"
                  icon="DeleteIcon"
                  color="red"
                  class="cursor-pointer mr-1"
                  size="16"
                  @click="resetItem(props.row)"
                />
                <b-tooltip
                  title="Annuler"
                  class="cursor-pointer"
                  :target="`invoice-row-${props.row.id}-show-icon`"
                />

              </span>
            </span>

            <!-- Column: Common -->

            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>

          <!-- pagination -->
          <template
            slot="pagination-bottom"
            slot-scope="props"
          >
            <div class="d-flex justify-content-between flex-wrap">
              <div class="d-flex align-items-center mb-0 mt-1">
                <span class="text-nowrap">
                  Affichage 1 à
                </span>
                <b-form-select
                  v-model="pageLength"
                  :options="['50','100','500']"
                  class="mx-1"
                  @input="(value)=>props.perPageChanged({currentPerPage:value})"
                />
                <span class="text-nowrap "> de {{ props.total }} résultats trouvés </span>
              </div>
              <div>
                <b-pagination
                  :value="1"
                  :total-rows="props.total"
                  :per-page="pageLength"
                  first-number
                  last-number
                  align="right"
                  prev-class="prev-item"
                  next-class="next-item"
                  class="mt-1 mb-0"
                  @input="(value)=>props.pageChanged({currentPage:value})"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </div>
            </div>
          </template>

        </vue-good-table>
        <b-form-group
          v-if="drivers.length > 0"
          label="Sélectionner le livreur"
          label-for="select-livreur"
          aria-label="Sélection du livreur pour la run sheet"
        >
          <validation-provider
            #default="{ errors }"
            name="Livreur"
          >
            <v-select
              v-model="livreur"
              :clearable="false"
              placeholder="Livreur"
              label="username"
              :options="drivers"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>
        <b-form-group
          label="Sélectionner la voiture"
          label-for="select-voiture"
          aria-label="Sélection de la voiture pour la run sheet"
        >
          <validation-provider
            #default="{ errors }"
            name="Voiture"
          >
            <v-select
              v-model="livreur.car"
              :clearable="false"
              placeholder="Voiture"
              label="serial_number"
              :options="cars"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>
        <b-form-group class="d-flex justify-content-end">
          <span id="save-roadmap-btn-wrapper">
            <b-button
              ref="saveButton"
              variant="gradient-primary"
              class="ml-1 d-inline-block save-btn"
              :class="{ 'not-allowed': isLoading || !isRoadmapValid }"
              :disabled="isLoading || !isRoadmapValid"
              aria-label="Créer la run sheet avec les colis sélectionnés"
              @click="addRoadMap"
            >
              <feather-icon icon="PlusIcon" />
              <span class="ml-1">Enregistrer</span>
            </b-button>
            <b-tooltip
              v-if="isLoading || !isRoadmapValid"
              target="save-roadmap-btn-wrapper"
              triggers="hover focus"
              placement="top"
            >
              <template v-if="isLoading">
                Veuillez patienter, chargement en cours...
              </template>
              <template v-else>
                <span v-if="selected.length === 0">Veuillez sélectionner au moins un colis.</span>
                <span v-else-if="!livreur || !livreur.id">Veuillez sélectionner un livreur.</span>
                <span v-else>Veuillez remplir tous les champs obligatoires.</span>
              </template>
            </b-tooltip>
          </span>
        </b-form-group>
      </b-card>
    </div>
    <div
      class="sr-only"
      aria-live="polite"
    >
      Veuillez remplir tous les champs obligatoires pour créer une run sheet. Les champs marqués d'un astérisque sont obligatoires.
    </div>
  </div>
</template>

<script>
import Ripple from 'vue-ripple-directive'
import axios from 'axios'
import { orderMatchesReference } from '@/utils/orderReference'
import vSelect from 'vue-select'
import storeAuth from '@/store/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  BCard, BButton, BPagination, BFormGroup, BFormInput, BInputGroup, BInputGroupPrepend, BFormSelect, BTooltip, BSpinner,

} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import { ValidationProvider } from 'vee-validate'
import zeroTentative from '@/assets/audio/0tentative.mp3'
import oneTentative from '@/assets/audio/1tentative.mp3'
import twoTentative from '@/assets/audio/2tentative.mp3'
import threeTentative from '@/assets/audio/3tentative.mp3'
import fourTentative from '@/assets/audio/4tentative.mp3'
import fiveTentative from '@/assets/audio/5tentative.mp3'
import isAnExchange from '@/assets/audio/exchange.mp3'
import ok from '@/assets/audio/ok.mp3'
import errorVoice from '@/assets/audio/error.mp3'
import alreadyScanned from '@/assets/audio/colis-deja-scanne.mp3'
import rendezVous from '@/assets/audio/rendez-vous.mp3'

export default {
  name: 'CreateRoadmap',
  components: {
    BButton,
    ValidationProvider,
    VueGoodTable,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BFormSelect,
    BPagination,
    BTooltip,
    vSelect,
    BCard,
    BSpinner,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      isLoading: false,
      drivers: [],
      livreur: {},
      date: '01-10-2021',
      status: '',
      pageLength: 100,
      dir: false,
      columns: [
        {
          label: 'Action',
          field: 'action',
        },
        {
          label: 'BL',
          field: 'id',
        },
        {
          label: 'Client',
          field: 'name',
          filterOptions: {
            enabled: true,
            placeholder: 'chercher client',
          },
        },
        {
          label: 'Fournisseur',
          field: 'supplier.username',
          filterOptions: {
            enabled: true,
            placeholder: 'Fournisseur',
          },
        },
        {
          label: 'Gouvernorat',
          field: 'governorate',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher gouvernorat',
          },
        },
        {
          label: 'Délégation',
          field: 'delegation',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher délégation',
          },
        },
        {
          label: 'Adresse',
          field: 'address',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher adresse',
          },
        },
        {
          label: 'tel',
          field: 'phone1',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher tel',
          },
        },
      ],
      role: '',
      orders: [],
      selected: [],
      searchTerm: '',
      ref: '',
      user: {},
      cars: [],
      car: {},
      repository: {},
      repositories: [],
      zeroTentative,
      oneTentative,
      twoTentative,
      threeTentative,
      fourTentative,
      fiveTentative,
      isAnExchange,
      ok,
      errorVoice,
      alreadyScanned,
      rendezVous,
    }
  },
  computed: {
    isRoadmapValid() {
      return (
        this.livreur
        && this.livreur.id
        && this.selected.length > 0
      )
    },
  },
  created() {
    this.user = storeAuth.state.user
    if (this.user.role === 'manager') {
      this.getDepositOrder()
    }
    this.getRepositories()
    this.getDeliveryMans()
    this.getCars()
  },
  mounted() {
    this.$refs.select.focus()
  },
  methods: {
    async checkOrder() {
      const existingOrder = this.selected.find(item => orderMatchesReference(item, this.ref))

      if (existingOrder) {
        await this.withTitle('Colis déjà scanné sur la liste sélectionnée')
        this.playAudio(this.alreadyScanned)
      } else {
        const searchedItem = this.orders.find(element => orderMatchesReference(element, this.ref))

        if (searchedItem) {
          // Play rendez-vous audio if status is 18
          if (searchedItem.status === 18) {
            await this.playAudio(this.rendezVous)
            await this.delay(500)
          }
          if (searchedItem.is_an_exchange) {
            await this.playAudio(this.isAnExchange)
            await this.delay(500)
          }
          await this.addItem(searchedItem)
          this.ref = ''
          if (searchedItem.return_number === 0) {
            this.playAudio(this.zeroTentative)
          } else if (searchedItem.return_number === 1) {
            this.playAudio(this.oneTentative)
          } else if (searchedItem.return_number === 2) {
            this.playAudio(this.twoTentative)
          } else if (searchedItem.return_number === 3) {
            this.playAudio(this.threeTentative)
          } else if (searchedItem.return_number === 4) {
            this.playAudio(this.fourTentative)
          } else if (searchedItem.return_number === 5) {
            this.playAudio(this.fiveTentative)
          }
        } else {
          this.playAudio(this.errorVoice)
          this.ref = ''
        }
      }
    },
    async playAudio(payload) {
      const voice = new Audio(payload)
      voice.addEventListener('canplaythrough', () => {
        voice.play()
      })
    },
    async delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },
    async addItem(item) {
      this.selected.push(item)
      const i = this.orders.findIndex(element => element.id === item.id)
      this.orders.splice(i, 1)
    },
    async resetItem(item) {
      this.orders.push(item)
      const i = this.selected.findIndex(element => element.id === item.id)
      this.selected.splice(i, 1)
    },
    async getRepositories() {
      const { data } = await axios.get('/api/repositories/')
      this.repositories = data
    },
    async getCars() {
      try {
        const { data } = await axios.get('/api/cars/dropdown/')
        this.cars = data
      } catch (error) {
        this.cars = []
      }
    },
    async withTitle(text) {
      await this.$swal({
        title: 'Alerte',
        icon: 'warning',
        text,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      })
      this.ref = ''
    },
    async addRoadMap() {
      // Prepare summary for confirmation dialog
      const summary = `Résumé de la création :\n\n- Livreur : ${this.livreur?.username || ''}\n- Véhicule : ${this.livreur?.car?.serial_number || ''}\n- Nombre de colis : ${this.selected.length}`
      const confirmed = await this.$swal({
        title: 'Confirmer la création',
        text: summary,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Oui, créer',
        cancelButtonText: 'Annuler',
        customClass: {
          confirmButton: 'btn btn-success',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      })
      if (!confirmed.isConfirmed) return

      this.isLoading = true
      const covertedOrders = this.selected.map(order => order.id)

      const obj = {
        orders: covertedOrders,
        delivery_man_id: this.livreur.id,
      }

      // Include car_id if a car is selected
      if (this.livreur.car && this.livreur.car.id) {
        obj.car_id = this.livreur.car.id
      }

      // Only include repository_id for admin and superadmin users
      if (storeAuth.state.user.role === 'admin' || storeAuth.state.user.role === 'superadmin') {
        if (!this.repository || !this.repository.id) {
          this.showToast('danger', 'top-center', 'Veuillez sélectionner un dépôt')
          this.isLoading = false
          return
        }
        obj.repository_id = this.repository.id
      }

      try {
        const { data } = await axios.post('/api/roadmaps/create-roadmap/', obj)
        this.showToast('success', 'top-center', 'Run sheet ajouté avec succés')
        this.$router.push(`/roadmaps/show/${data}`)
      } catch (error) {
        const errorData = error.response?.data
        if (errorData?.error === 'Conflit de véhicule') {
          this.$swal({
            title: 'Conflit de véhicule',
            html: `
              <div class="text-left">
                <p><strong>Erreur:</strong> Un livreur ne peut pas conduire deux véhicules différents en même temps.</p>
                <hr/>
                <p><strong>Feuille de route existante:</strong> #${errorData.conflicting_roadmap_id}</p>
                <p><strong>Véhicule assigné:</strong> ${errorData.conflicting_vehicle}</p>
                <hr/>
                <p class="text-muted">Veuillez utiliser le même véhicule ou payer la feuille de route existante avant d'en créer une nouvelle avec un autre véhicule.</p>
              </div>
            `,
            icon: 'error',
            customClass: {
              confirmButton: 'btn btn-primary',
            },
            buttonsStyling: false,
          })
        } else {
          this.showToast('danger', 'top-center', errorData?.detail || errorData?.error || error.toString())
        }
      } finally {
        this.isLoading = false
      }
    },
    formatDate(date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      return `${day1.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`
    },
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
    async getDeliveryMans() {
      this.isLoading = true
      try {
        const { data } = await axios.get('api/auth/users/drivers-for-roadmap/', {
          params: { repository_id: this.repository?.id || this.user.repository },
        })
        this.drivers = data
      } catch (error) {
        this.drivers = []
      }
      this.isLoading = false
    },
    async getDepositOrder() {
      this.isLoading = true
      this.livreur = {}
      await this.getDeliveryMans()
      this.selected = []
      let repository = null
      if (storeAuth.state.user.role === 'admin' || storeAuth.state.user.role === 'superadmin') {
        repository = this.repository.id
      } else {
        repository = storeAuth.state.user.repository
      }
      try {
        const { data } = await axios.get(`/api/orders/orders-allowed-for-roadmap/${repository}`)
        this.orders = data
      } catch (error) {
        // handle error if needed
      }
      this.isLoading = false
    },
    formatFn(date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const h = date.slice(11, 19)
      return `${year}-${month.padStart(2, '0')}-${day1.padStart(2, '0')} | ${h}`
    },

  },
}
</script>

<style lang="scss" >
@import "@core/scss/vue/libs/vue-select.scss";
@import '@core/scss/vue/libs/vue-good-table.scss';

// Accessibilité : focus visible pour les champs interactifs
input:focus, select:focus, .v-select:focus {
  outline: 2px solid #005fcc !important;
  outline-offset: 2px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  border: 0;
}

.save-btn.not-allowed,
.save-btn[disabled] {
  cursor: not-allowed !important;
  opacity: 0.6 !important;
  pointer-events: auto !important;
}

</style>
